import React, { useState } from "react";
import { FaCloudUploadAlt, FaDownload, FaInfoCircle, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { useTheme, getT } from "./ThemeContext";
import API_BASE from "./apiConfig";
const BASE = API_BASE;

export default function BatchBulkUpload({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [responseMsg, setResponseMsg] = useState(null);
  const [errorList, setErrorList] = useState([]);
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResponseMsg(null);
      setErrorList([]);
    }
  };

  const downloadTemplate = () => {
    window.location.href = `${BASE}/batches/bulk/template`;
  };

  const handleUpload = async () => {
    if (!file) {
      setResponseMsg({ ok: false, text: "Please select a file to upload." });
      return;
    }
    
    setIsUploading(true);
    setResponseMsg(null);
    setErrorList([]);
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("updated_by", currentUser?.full_name || "system");
    
    try {
      const res = await fetch(`${BASE}/batches/bulk/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      
      if (data.success) {
        setResponseMsg({ ok: true, text: data.message });
        setFile(null);
        document.getElementById("batch-upload-input").value = "";
      } else {
        setResponseMsg({ ok: false, text: data.message });
        if (data.errors) setErrorList(data.errors);
      }
    } catch (e) {
      console.error(e);
      setResponseMsg({ ok: false, text: "Network error. Failed to connect to the server." });
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <div style={{ padding: "20px 28px 48px", boxSizing: "border-box", fontFamily: T.font, minHeight: "100%", background: "transparent" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        
        <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "32px" }}>
          <div style={{
            width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0,
            background: `linear-gradient(135deg, ${T.yellow || "#f7ce0b"}, #fff3a0)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 14px rgba(255,200,0,0.32)",
          }}>
            <FaCloudUploadAlt size={20} color={T.blue || "#034e7b"} />
          </div>
          <div>
            <h2 style={{ fontSize: "22px", fontWeight: 800, color: T.textPrimary, margin: 0, letterSpacing: "-0.01em" }}>
              Bulk Upload - Batches & Enrollments
            </h2>
            <p style={{ margin: 0, fontSize: "12px", color: T.textMuted, marginTop: "1px", lineHeight: 1.5, maxWidth: "600px" }}>
              Upload a CSV template to create multiple batches and enroll students simultaneously. The system uses transaction rollback, ensuring that any validation errors will cancel the entire upload, preventing partial or corrupt data.
            </p>
          </div>
        </div>
        
        <div style={{ 
          background: T.glass, border: T.glassBdr, borderRadius: 16, 
          padding: 24, marginBottom: 24, boxShadow: T.glassSh 
        }}>
          
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
            <button onClick={downloadTemplate} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 8,
              background: isDark ? "rgba(255,255,255,0.1)" : "rgba(3,78,123,0.1)", 
              color: T.blue, border: `1.5px solid ${T.blue}`,
              fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s"
            }}>
              <FaDownload size={14} /> Download CSV Template
            </button>
            <div style={{ fontSize: 12, color: T.textMuted, flex: 1 }}>
              <FaInfoCircle style={{ display: "inline", marginRight: 4, transform: "translateY(2px)" }} /> 
              Always use the official template schema to avoid upload errors.
            </div>
          </div>
          
          <hr style={{ border: 0, borderTop: T.glassBdr, margin: "0 0 24px 0" }} />
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textPrimary, marginBottom: 8 }}>
              Select CSV File
            </label>
            <input 
              id="batch-upload-input"
              type="file" 
              accept=".csv"
              onChange={handleFileChange}
              style={{
                display: "block", width: "100%", padding: "10px", 
                border: `1.5px dashed ${file ? T.blue : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)")}`, 
                borderRadius: 8, background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                color: T.textPrimary, outline: "none", cursor: "pointer", transition: "all 0.2s"
              }}
            />
          </div>
          
          <button 
            disabled={!file || isUploading}
            onClick={handleUpload} 
            style={{
              width: "100%", padding: "12px", borderRadius: 8, border: "none",
              background: file ? T.blue : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"), 
              color: file ? "#fff" : T.textMuted,
              fontSize: 14, fontWeight: 600, display: "flex", justifyContent: "center", alignItems: "center", gap: 8,
              cursor: file && !isUploading ? "pointer" : "not-allowed", transition: "all 0.2s"
            }}
          >
            <FaCloudUploadAlt size={16} /> 
            {isUploading ? "Uploading & Processing..." : "Upload & Execute"}
          </button>
        </div>

        {/* Global Response Msg */}
        {responseMsg && (
          <div style={{ 
            padding: 16, borderRadius: 10, marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start",
            background: responseMsg.ok ? "rgba(0,230,118,0.12)" : "rgba(255,77,77,0.12)",
            border: `1.5px solid ${responseMsg.ok ? "rgba(0,230,118,0.3)" : "rgba(255,77,77,0.3)"}`,
            color: responseMsg.ok ? (isDark ? "#69f0ae" : "#00c853") : (isDark ? "#ff5252" : "#d50000")
          }}>
            <div style={{ marginTop: 2 }}>{responseMsg.ok ? <FaCheckCircle size={16} /> : <FaExclamationCircle size={16} />}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{responseMsg.text}</div>
            </div>
          </div>
        )}

        {/* List of Validation Errors if any */}
        {errorList.length > 0 && (
          <div style={{ background: T.glass, border: `1.5px solid rgba(255,77,77,0.3)`, borderRadius: 12, padding: 16, overflow: "hidden" }}>
             <h3 style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#ff8a80" : "#d50000", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Validation Errors ({errorList.length})</h3>
             <div style={{ maxHeight: 250, overflowY: "auto", paddingRight: 8 }}>
               {errorList.map((err, i) => (
                 <div key={i} style={{ 
                   padding: "8px", borderBottom: i === errorList.length - 1 ? "none" : T.glassBdr,
                   color: T.textPrimary, fontSize: 13, fontFamily: "monospace", display: "flex", gap: 10
                 }}>
                   <span style={{ color: "#ff5252", fontWeight: 700 }}>✗</span> {err}
                 </div>
               ))}
             </div>
          </div>
        )}

      </div>
    </div>
  );
}
